/**
 * Nether mob models (vanilla geometry):
 *  - piglin / zombified piglin: HumanoidModel body with PiglinModel's head
 *    (10x8x8 skull, snout, tusks, floppy ears), holding a golden sword.
 *  - magma cube: MagmaCubeModel's 8 one-pixel slices + inner core; slices
 *    spread apart with the squish factor, whole model scaled by the size.
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import type { MobView } from '../../game/types';
import { buildPart, entityMaterial, sheetTexture, type EntityMat, type CubeDef } from './core';
import { BLOCKS } from '../../blocks';
import { Humanoid } from './humanoid';
import type { QuadPose } from './mobs';
import { applyHandheld, itemSpriteMesh } from './extras';

export interface NetherAnim {
  parts: THREE.Object3D;
  mats: EntityMat[];
  animate(v: MobView, q: QuadPose, headY: number, headX: number, age: number): void;
  dispose(): void;
}

const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false): CubeDef =>
  ({ tex, from, size, inflate, mirror });

export function piglinAnim(env: EnvUniforms, sheet: string, zombified: boolean, weapon = 'golden_sword'): NetherAnim {
  const h = new Humanoid(env, { sheet, modern: true, tw: 64, th: 64 });
  // swap the 8x8x8 head (+ hat layer) for the piglin skull
  for (const c of h.head.children) c.visible = false;
  const mat = h.mats[0];
  h.head.add(buildPart({
    cubes: [
      cube([0, 0], [-5, -8, -4], [10, 8, 8]),
      cube([31, 1], [-2, -4, -5], [4, 4, 1]),
      cube([2, 4], [2, -2, -5], [1, 2, 1]),
      cube([2, 0], [-3, -2, -5], [1, 2, 1]),
    ],
  }, 64, 64, mat));
  const leftEar = buildPart({ cubes: [cube([51, 6], [0, 0, -2], [1, 5, 4])], pos: [4.5, -6, 0] }, 64, 64, mat);
  const rightEar = buildPart({ cubes: [cube([39, 6], [-1, 0, -2], [1, 5, 4])], pos: [-4.5, -6, 0] }, 64, 64, mat);
  h.head.add(leftEar, rightEar);
  const sword = itemSpriteMesh(env, weapon);
  applyHandheld(sword.mesh, false);
  h.rightHand.add(sword.mesh);
  h.mats.push(sword.mat);
  // bartering: the piglin admires a gold ingot held up in the (visible) right hand (v.color = 1)
  const ingot = itemSpriteMesh(env, 'gold_ingot');
  applyHandheld(ingot.mesh, false);
  h.rightHand.add(ingot.mesh);
  ingot.mesh.visible = false;
  h.mats.push(ingot.mat);
  return {
    parts: h.root, mats: h.mats,
    animate(v, q, headY, headX, age) {
      const admiring = !zombified && v.color === 1;
      sword.mesh.visible = v.heldItem !== 0 && !admiring;
      ingot.mesh.visible = admiring;
      h.animate({
        limbSwing: q.limbSwing, limbAmt: q.limbAmt, headY, headX, age,
        attack: v.attackAnim, zombieArms: zombified && v.aggressive, bow: false, holding: v.heldItem !== 0 && !(zombified && v.aggressive),
        sneaking: false, swimming: false, bob: true,
      });
      if (admiring) {
        // PiglinModel.ADMIRING_ITEM: arm up in front, head tipped down at the ingot
        h.rightArm.rotation.x = -0.9; h.rightArm.rotation.y = -0.3;
        h.head.rotation.x = 0.5; h.head.rotation.y = 0;
      }
      // PiglinModel ear flop
      const flop = Math.cos(age * 0.1 + q.limbSwing * 0.5) * 0.08 * (1 + q.limbAmt);
      leftEar.rotation.z = -0.5236 - flop;
      rightEar.rotation.z = 0.5236 + flop;
    },
    dispose() { for (const m of h.mats) m.dispose(); },
  };
}

export function magmaCubeAnim(env: EnvUniforms): NetherAnim {
  const parts = new THREE.Group();
  // magmacube.png is 64x64 (1.21 layout); vanilla renders it fullbright (emissive adds x1.4, so tint down)
  const mat = entityMaterial(env, sheetTexture('entity/slime/magmacube'), { emissive: true, tint: 0xb6b6b6 });
  const segs: THREE.Group[] = [];
  for (let i = 0; i < 8; i++) {
    // 26.x sheet: slices 0-3 stacked at u=0, 4-7 at u=32, 9 rows each (8 top/bottom + 1 side)
    const u = i < 4 ? 0 : 32, v = (i % 4) * 9;
    const g = buildPart({ cubes: [cube([u, v], [-4, 16 + i, -4], [8, 1, 8])] }, 64, 64, mat);
    parts.add(g);
    segs.push(g);
  }
  parts.add(buildPart({ cubes: [cube([24, 40], [-2, 18, -2], [4, 4, 4])] }, 64, 64, mat));
  return {
    parts, mats: [mat],
    animate(v) {
      const size = v.color > 0 ? v.color : 4;
      const squish = Math.max(0, v.swell);
      for (let i = 0; i < 8; i++) segs[i].position.y = -(4 - i) * squish * 1.7;
      // vanilla scales the 8px cube by size (0.999 * size), feet stay at y = 24
      const s = 0.999 * size;
      parts.scale.set(s, s, s);
      parts.position.y = 24 * (1 - s);
    },
    dispose() { mat.dispose(); },
  };
}

// ------------------------------------------------------------- round 2 models

/** Ghast: 16px cube body + 9 tentacles (GhastModel), drawn 4.5x; mouth texture while shooting. */
export function ghastAnim(env: EnvUniforms): NetherAnim {
  const parts = new THREE.Group();
  const idle = sheetTexture('entity/ghast/ghast');
  const shoot = sheetTexture('entity/ghast/ghast_shooting');
  const mat = entityMaterial(env, idle);
  const inner = new THREE.Group();
  inner.add(buildPart({ cubes: [cube([0, 0], [-8, -8, -8], [16, 16, 16])], pos: [0, 8, 0] }, 64, 32, mat));
  const tent: THREE.Group[] = [];
  // deterministic tentacle lengths (vanilla rolls 8..14 once per entity)
  const LEN = [11, 9, 13, 8, 12, 10, 14, 9, 12];
  for (let i = 0; i < 9; i++) {
    const x = (((i % 3) - (Math.floor(i / 3) % 2) * 0.5 + 0.25) / 2 * 2 - 1) * 5;
    const z = (Math.floor(i / 3) / 2 * 2 - 1) * 5;
    const g = buildPart({ cubes: [cube([0, 0], [-1, 0, -1], [2, LEN[i], 2])], pos: [x, 15, z] }, 64, 32, mat);
    inner.add(g);
    tent.push(g);
  }
  // hitbox is 4 tall: scale the 16+14 px model about its top so tentacles dangle below
  inner.scale.setScalar(4.5);
  inner.position.y = 24 - 4.5 * 16 - 4.5 * 4;
  parts.add(inner);
  return {
    parts, mats: [mat],
    animate(v, _q, _hy, _hx, age) {
      mat.uniforms.uMap.value = v.aggressive ? shoot : idle;
      for (let i = 0; i < 9; i++) tent[i].rotation.x = 0.2 * Math.sin(age * 0.3 + i) + 0.4;
      inner.position.y = 24 - 4.5 * 20 + Math.sin(age * 0.05) * 2;
    },
    dispose() { mat.dispose(); },
  };
}

/** Blaze: head + 12 rotating rods in three rings (BlazeModel), emissive. */
export function blazeAnim(env: EnvUniforms): NetherAnim {
  const parts = new THREE.Group();
  const mat = entityMaterial(env, sheetTexture('entity/blaze/blaze'), { emissive: true });
  const head = buildPart({ cubes: [cube([0, 0], [-4, -4, -4], [8, 8, 8])], pos: [0, 4, 0] }, 64, 32, mat);
  parts.add(head);
  const rods: THREE.Group[] = [];
  for (let i = 0; i < 12; i++) {
    const g = buildPart({ cubes: [cube([0, 16], [0, 0, 0], [2, 8, 2])] }, 64, 32, mat);
    parts.add(g);
    rods.push(g);
  }
  return {
    parts, mats: [mat],
    animate(_v, _q, headY, headX, age) {
      // BlazeModel.setupAnim, with y measured down from the head (feet at 24)
      let f = age * Math.PI * -0.1;
      for (let i = 0; i < 4; i++) {
        rods[i].position.set(Math.cos(f) * 9, 1 + Math.cos((i * 2 + age) * 0.25) + 6, Math.sin(f) * 9);
        f++;
      }
      f = Math.PI / 4 + age * Math.PI * 0.03;
      for (let i = 4; i < 8; i++) {
        rods[i].position.set(Math.cos(f) * 7, 2 + Math.cos((i * 2 + age) * 0.25) + 6, Math.sin(f) * 7);
        f++;
      }
      f = 0.47123894 + age * Math.PI * -0.05;
      for (let i = 8; i < 12; i++) {
        rods[i].position.set(Math.cos(f) * 5, 11 + Math.cos((i * 1.5 + age) * 0.5) + 6, Math.sin(f) * 5);
        f++;
      }
      head.rotation.set(headX, headY, 0);
    },
    dispose() { mat.dispose(); },
  };
}

/** Wither skeleton: skeleton model, wither texture, 1.2x, stone sword. */
export function witherSkeletonAnim(env: EnvUniforms): NetherAnim {
  const h = new Humanoid(env, { sheet: 'entity/skeleton/wither_skeleton', modern: false, thin: true, tw: 64, th: 32 });
  const sword = itemSpriteMesh(env, 'stone_sword');
  applyHandheld(sword.mesh, false);
  h.rightHand.add(sword.mesh);
  h.mats.push(sword.mat);
  const parts = new THREE.Group();
  parts.add(h.root);
  parts.scale.setScalar(1.2);
  parts.position.y = 24 * (1 - 1.2);
  return {
    parts, mats: h.mats,
    animate(v, q, headY, headX, age) {
      h.animate({
        limbSwing: q.limbSwing, limbAmt: q.limbAmt, headY, headX, age, attack: v.attackAnim,
        zombieArms: false, bow: false, holding: true, sneaking: false, swimming: false, bob: true,
      });
    },
    dispose() { for (const m of h.mats) m.dispose(); },
  };
}

/** Hoglin (HoglinModel, 128x64 sheet). */
export function hoglinAnim(env: EnvUniforms, sheet = 'entity/hoglin/hoglin'): NetherAnim {
  const parts = new THREE.Group();
  const mat = entityMaterial(env, sheetTexture(sheet));
  const P = (d: Parameters<typeof buildPart>[0], parent: THREE.Object3D = parts): THREE.Group => {
    const g = buildPart(d, 128, 64, mat); parent.add(g); return g;
  };
  const body = P({ cubes: [cube([1, 1], [-8, -7, -13], [16, 14, 26])], pos: [0, 7, 0] });
  P({ cubes: [cube([90, 33], [0, 0, -9], [0, 10, 19], 0.001)], pos: [0, -14, -5] }, body);
  const head = P({ cubes: [cube([61, 1], [-7, -3, -19], [14, 6, 19])], pos: [0, 2, -12], rot: [0.87266, 0, 0] });
  P({ cubes: [cube([1, 1], [-6, -1, -2], [6, 1, 4])], pos: [-6, -2, -3], rot: [0, 0, -0.6981] }, head);
  P({ cubes: [cube([1, 6], [0, -1, -2], [6, 1, 4])], pos: [6, -2, -3], rot: [0, 0, 0.6981] }, head);
  P({ cubes: [cube([10, 13], [-1, -11, -1], [2, 11, 2])], pos: [-7, 2, -12] }, head);
  P({ cubes: [cube([1, 13], [-1, -11, -1], [2, 11, 2])], pos: [7, 2, -12] }, head);
  const legs = [
    P({ cubes: [cube([66, 42], [-3, 0, -3], [6, 14, 6])], pos: [-4, 10, -8.5] }),
    P({ cubes: [cube([41, 42], [-3, 0, -3], [6, 14, 6])], pos: [4, 10, -8.5] }),
    P({ cubes: [cube([21, 45], [-2.5, 0, -2.5], [5, 11, 5])], pos: [-5, 13, 10] }),
    P({ cubes: [cube([0, 45], [-2.5, 0, -2.5], [5, 11, 5])], pos: [5, 13, 10] }),
  ];
  return {
    parts, mats: [mat],
    animate(v, q) {
      const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.2;
      legs[0].rotation.x = Math.cos(s) * a; legs[1].rotation.x = Math.cos(s + Math.PI) * a;
      legs[2].rotation.x = Math.cos(s + Math.PI) * a; legs[3].rotation.x = Math.cos(s) * a;
      // head swing on attack
      head.rotation.x = 0.87266 - v.attackAnim * 0.8;
    },
    dispose() { mat.dispose(); },
  };
}

/** Strider (StriderModel, 64x128); cold texture when color = 1. */
export function striderAnim(env: EnvUniforms): NetherAnim {
  const parts = new THREE.Group();
  const warm = sheetTexture('entity/strider/strider');
  const cold = sheetTexture('entity/strider/strider_cold');
  const mat = entityMaterial(env, warm);
  const body = buildPart({ cubes: [cube([0, 0], [-8, -6, -8], [16, 14, 16])], pos: [0, 1, 0] }, 64, 128, mat);
  const rl = buildPart({ cubes: [cube([0, 32], [-2, 0, -2], [4, 16, 4])], pos: [-4, 8, 0] }, 64, 128, mat);
  const ll = buildPart({ cubes: [cube([0, 55], [-2, 0, -2], [4, 16, 4])], pos: [4, 8, 0] }, 64, 128, mat);
  parts.add(body, rl, ll);
  // saddle layer (equipment/strider_saddle): the body, inflated
  const satMat = entityMaterial(env, sheetTexture('entity/equipment/strider_saddle/saddle'));
  const sat = buildPart({ cubes: [cube([0, 0], [-8, -6, -8], [16, 14, 16], 0.5)] }, 64, 128, satMat);
  sat.visible = false;
  body.add(sat);
  return {
    parts, mats: [mat, satMat],
    animate(v, q, _hy, _hx, age) {
      sat.visible = !!v.saddled;
      const isCold = v.color === 1;
      mat.uniforms.uMap.value = isCold ? cold : warm;
      const s = q.limbSwing * 1.5, a = Math.min(q.limbAmt * 2, 1);
      rl.rotation.x = Math.sin(s) * 2 * a; ll.rotation.x = Math.sin(s + Math.PI) * 2 * a;
      body.rotation.z = Math.sin(s) * 0.1 * a + (isCold ? Math.sin(age * 2.6) * 0.045 : 0); // cold: shivers
      body.position.y = 1 - Math.cos(s * 2) * 2 * a;
    },
    dispose() { mat.dispose(); },
  };
}

/** Enderman: long-limbed humanoid with emissive purple eyes. */
export function endermanAnim(env: EnvUniforms): NetherAnim {
  const parts = new THREE.Group();
  const mat = entityMaterial(env, sheetTexture('entity/enderman/enderman'));
  const eyes = entityMaterial(env, sheetTexture('entity/enderman/enderman_eyes'), { emissive: true });
  const P = (d: Parameters<typeof buildPart>[0], m = mat, parent: THREE.Object3D = parts): THREE.Group => {
    const g = buildPart(d, 64, 32, m); parent.add(g); return g;
  };
  const head = P({ cubes: [cube([0, 0], [-4, -8, -4], [8, 8, 8])], pos: [0, -14, 0] });
  P({ cubes: [cube([0, 0], [-4, -8, -4], [8, 8, 8], 0.01)] }, eyes, head);
  P({ cubes: [cube([32, 16], [-4, 0, -2], [8, 12, 4])], pos: [0, -14, 0] });
  const ra = P({ cubes: [cube([56, 0], [-1, -2, -1], [2, 30, 2])], pos: [-5, -12, 0] });
  const la = P({ cubes: [cube([56, 0], [-1, -2, -1], [2, 30, 2], 0, true)], pos: [5, -12, 0] });
  const rl = P({ cubes: [cube([56, 0], [-1, 0, -1], [2, 30, 2])], pos: [-2, -6, 0] });
  const ll = P({ cubes: [cube([56, 0], [-1, 0, -1], [2, 30, 2], 0, true)], pos: [2, -6, 0] });
  // carried block: a 10px cube held out in front (vanilla CarriedBlockLayer), textured per block
  const held = new THREE.Mesh(new THREE.BoxGeometry(10, 10, 10), mat);
  held.position.set(0, -2, -9);
  held.visible = false;
  parts.add(held);
  const mats: EntityMat[] = [mat, eyes];
  let heldId = 0;
  return {
    parts, mats,
    animate(v, q, headY, headX) {
      if (v.color !== heldId) {
        heldId = v.color;
        held.visible = heldId > 0;
        if (heldId > 0) {
          const m = entityMaterial(env, sheetTexture(blockTextureName(heldId)));
          if (held.material !== mat) { (held.material as EntityMat).dispose(); mats.pop(); }
          held.material = m;
          mats.push(m);
        }
      }
      if (heldId > 0) { ra.rotation.x = -0.5; la.rotation.x = -0.5; }
      const s = q.limbSwing * 0.6662, a = Math.min(q.limbAmt, 0.4);
      rl.rotation.x = Math.cos(s) * 1.4 * a; ll.rotation.x = Math.cos(s + Math.PI) * 1.4 * a;
      if (heldId === 0) { ra.rotation.x = Math.cos(s + Math.PI) * a - v.attackAnim; la.rotation.x = Math.cos(s) * a; }
      head.rotation.set(headX, headY, 0);
      head.position.y = v.aggressive ? -19 : -14; // mouth-open "scream" pose lifts the head
    },
    dispose() { for (const m of mats) m.dispose(); },
  };
}

/** Block texture file (public/mc/<name>.png) for a carried block's sides. */
function blockTextureName(id: number): string {
  const n = (BLOCKS[id]?.name ?? 'dirt').toLowerCase().replace(/ /g, '_').replace(/'/g, '');
  const SIDE: Record<string, string> = {
    grass_block: 'grass_block_side', mycelium: 'mycelium_side', podzol: 'podzol_side', pumpkin: 'pumpkin_side',
    melon: 'melon_side', tnt: 'tnt_side', crimson_nylium: 'crimson_nylium_side', warped_nylium: 'warped_nylium_side',
    moss_block: 'moss_block',
  };
  return SIDE[n] ?? n;
}
